import { useSelector } from "react-redux"
import { BasketAnalyzeDetails } from "../reducer/orders.reducer"
import { selectBasketAnalyzeDetails, selectOrdersData } from "../selectors/orders"
import { useEffect, useMemo, useState } from "react"
import { matchPath, useLocation } from "react-router-dom"
import { OptionChainViewKey } from "../assets/constants"
import ROUTES from "../assets/routes"
import { selectSimulatorOrders, selectSimulatorOrdersPrices } from "../selectors/simulator"

export const useCombineBasketAnalyzeAndLegOrders = () => {
    const orderData = useSelector(selectOrdersData)
    const location = useLocation()
    const [viewKey, setViewKey] = useState(matchPath(ROUTES.simulator, location.pathname) ? OptionChainViewKey.HISTORICAL : OptionChainViewKey.LIVE)
    const basketAnalyzeDetails: BasketAnalyzeDetails | undefined = useSelector(selectBasketAnalyzeDetails)
    const { orders: normalAllOrders = [], ordersPrices: normalAllOrdersPrices = [], parentSquareOffOrders: legParentSquareOffOrders = [], orderState, basketId, orderSecurityKey } = orderData
    const simulatorOrders = useSelector(selectSimulatorOrders)
    const simulatorOrdersPrices = useSelector(selectSimulatorOrdersPrices)
    const allOrders = useMemo(()=>viewKey===OptionChainViewKey.HISTORICAL ? simulatorOrders : normalAllOrders, [normalAllOrders, simulatorOrders, viewKey])
    const allOrdersPrices = useMemo(()=>viewKey===OptionChainViewKey.HISTORICAL ? simulatorOrdersPrices : normalAllOrdersPrices, [normalAllOrdersPrices, simulatorOrdersPrices, viewKey])
    const legOrders = useMemo(() => allOrders.filter(order => order.visibility), [allOrders])
    const legOrdersPrices = useMemo(() => allOrdersPrices.filter(order => order.visibility), [allOrdersPrices])

    useEffect(() => {
        if (matchPath(ROUTES.simulator, location.pathname)) {
            setViewKey(OptionChainViewKey.HISTORICAL)
        } else setViewKey(OptionChainViewKey.LIVE)
    }, [location.pathname])

    const { analyzeOrders = [], analyzeOrderPrizes = [], analyzeParentSquareOffOrders = [] } = useMemo(() => {
        if (!basketAnalyzeDetails || viewKey===OptionChainViewKey.HISTORICAL) {
            return {
                analyzeOrders: [],
                analyzeOrderPrizes: [],
                analyzeParentSquareOffOrders: []
            }
        }
        return {
            analyzeOrders: basketAnalyzeDetails.positionOrders,
            analyzeOrderPrizes: basketAnalyzeDetails.positionOrderPrices,
            analyzeParentSquareOffOrders: []
        }
    }, [basketAnalyzeDetails, viewKey])

    const orders = useMemo(() => [...analyzeOrders, ...legOrders].filter(o => o.visibility), [legOrders, analyzeOrders])
    const analyzePlusAllOrders = useMemo(() => [...analyzeOrders, ...allOrders], [allOrders, analyzeOrders])
    const analyzePlusAllOrderPrices = useMemo(() => [...analyzeOrderPrizes, ...legOrdersPrices], [analyzeOrderPrizes, legOrdersPrices])
    const ordersPrices = useMemo(() => [...analyzeOrderPrizes, ...legOrdersPrices].filter(o => o.visibility), [legOrdersPrices, analyzeOrderPrizes])
    const parentSquareOffOrders = useMemo(() => [...analyzeParentSquareOffOrders, ...legParentSquareOffOrders].filter(o => o.visibility), [legParentSquareOffOrders, analyzeParentSquareOffOrders])

    return {
        orders,
        analyzePlusAllOrders,
        analyzePlusAllOrderPrices,
        allOrders,
        allOrdersPrices,
        ordersPrices,
        parentSquareOffOrders,
        orderState,
        basketId,
        orderSecurityKey,
        analyzeOrders,
        distinct: Boolean(legOrders.length) && Boolean(basketAnalyzeDetails)
    }
}